import type { PackageStatus } from '@mypoz/shared';
import { STATUS_TONES } from '@mypoz/shared';
import { StatusChip } from '@mypoz/ui';

export interface PackageStatusChipProps {
  /** The lifecycle group, which decides the colour. */
  status: PackageStatus;
  /**
   * The vendor's own words for the exact state — `Package.statusLabel`, already resolved to the
   * reader's language. Not translated here: since MYP-40 the vendor publishes a label for each of
   * its twenty-two statuses, and passing them through says "Delayed" or "Arrived Hub" where this
   * app could only ever have said "On the way".
   */
  label: string;
}

/**
 * The package's stage — same dot-and-label recipe as `OrderStatusChip`, so the two lists read as
 * one visual language.
 *
 * Two of the six groups are coloured, and only two. `shipped` is the row's ordinary end state —
 * the package has left the warehouse for good — so it is worth spotting while scanning a long
 * list. Green rather than the mentor mockup's red: red is spoken for on this list by the
 * overdue-storage warning, and "shipped" is not a problem, so the completion colour used
 * everywhere else in this app reads truer than borrowing danger.
 *
 * `exception` is what red is for (MYP-40): lost, refused, destroyed, returned. Those are the four
 * states where something has gone wrong and the customer is the last to know, so they are the one
 * thing on this row that should stop a scanning eye.
 *
 * Everything else stays muted. Nothing in the middle of the track asks the customer to do
 * anything, and colouring each stage differently would spend attention on a position rather than
 * on a problem.
 */
export function PackageStatusChip({ status, label }: PackageStatusChipProps) {
  return <StatusChip tone={STATUS_TONES.package[status]}>{label}</StatusChip>;
}
